import React, { useState } from 'react';
import { DollarSign, Lock, CheckCircle } from 'lucide-react';
import { useAuth } from './AuthContext';
import { useNavigate } from 'react-router-dom';

const ResetPasswordPage = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const { updatePassword } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    try {
      setError('');
      setLoading(true);
      await updatePassword(password);
      setSuccess(true);
      
      // Redirect to home after 3 seconds
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (err) {
      console.error('Password update error:', err);
      setError('Error al actualizar la contraseña. Intenta nuevamente.');
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-800 dark:bg-black flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <div className="card-neon rounded-xl p-8 text-center">
            <div className="inline-block p-4 rounded-full bg-gradient-to-br from-teal-500/20 to-cyan-500/20 mb-4">
              <CheckCircle className="h-16 w-16 text-teal-300" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">¡Contraseña Actualizada!</h2>
            <p className="text-gray-400 mb-6">
              Tu contraseña ha sido restablecida exitosamente.
            </p>
            <p className="text-sm text-gray-500">
              Redirigiendo al inicio de sesión...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-800 dark:bg-black flex items-center justify-center px-4 relative overflow-hidden">
      {/* Animated background effect */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute w-96 h-96 bg-gradient-radial from-teal-500/20 to-transparent blur-3xl -top-48 -left-48 animate-pulse"></div>
        <div className="absolute w-96 h-96 bg-gradient-radial from-cyan-500/20 to-transparent blur-3xl -bottom-48 -right-48 animate-pulse" style={{animationDelay: '1s'}}></div>
      </div>

      <div className="max-w-md w-full relative z-10">
        {/* Logo and Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <DollarSign className="h-12 w-12 text-teal-300 mr-3" />
            <h1 className="text-4xl font-bold text-teal-300">La Banquita</h1>
          </div>
          <p className="text-lg text-gray-300">Restablecer Contraseña</p>
        </div>

        {/* Reset Password Card */}
        <div className="card-neon rounded-xl p-8">
          <div className="text-center mb-6">
            <div className="inline-block p-4 rounded-full bg-gradient-to-br from-teal-500/20 to-cyan-500/20 mb-4">
              <Lock className="h-12 w-12 text-teal-300" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Nueva Contraseña</h2>
            <p className="text-gray-400">
              Ingresa tu nueva contraseña
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-4 bg-red-500/10 border border-red-500/50 rounded-lg">
              <p className="text-red-400 text-sm text-center">{error}</p>
            </div>
          )}

          {/* Reset Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Nueva Contraseña</label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-5 w-5 text-teal-300" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  required
                  disabled={loading}
                  minLength={6}
                  className="w-full pl-10 pr-3 py-2 bg-gray-800 dark:bg-black border border-teal-500/30 text-white rounded-lg focus:border-teal-400 disabled:opacity-50 transition-all"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Confirmar Contraseña</label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-5 w-5 text-teal-300" />
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repite la contraseña"
                  required
                  disabled={loading}
                  minLength={6}
                  className="w-full pl-10 pr-3 py-2 bg-gray-800 dark:bg-black border border-teal-500/30 text-white rounded-lg focus:border-teal-400 disabled:opacity-50 transition-all"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-neon text-black font-bold py-3 px-4 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin-glow rounded-full h-5 w-5 border-2 border-black border-t-transparent mr-2"></div>
                  Actualizando...
                </div>
              ) : (
                'Restablecer Contraseña'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;